import { match } from 'react-router';
import { observe } from 'mobx';
import { IRootStore } from '@src/models';
import { ROUTES } from 'src/constants';

type HandlerArgs = {
  action: {
    tree: IRootStore;
  };
  match: match<{ jobId: string }>;
};

const jobFlow = {
  call: ({ action, match }: HandlerArgs) => {
    const { jobId } = match.params;
    jobId && action.tree.jobsStore.fetchJob(jobId);

    action.tree.resourcesStore.fetchAllThemes();
    action.tree.resourcesStore.getJobFunctions();
    action.tree.resourcesStore.getJobExperiences();
    action.tree.resourcesStore.getJobCurrencies();
    action.tree.resourcesStore.getJobSalaries();
    action.tree.resourcesStore.getJobEmploymentTypes();
  },
  abort: ({ action }: HandlerArgs) => {
    action.tree.resourcesStore.abort();
    action.tree.companyStore.abort();
    action.tree.jobsStore.abort();
  }
};

const jobsFlows = {
  [ROUTES.jobs.job]: {
    exact: false,
    call: ({ action, match }: HandlerArgs) => {
      const { jobId } = match.params;
      if (jobId === 'new') return;
      observe(
        action.tree.userStore,
        'isCandidate',
        change => {
          if (change.newValue) {
            action.tree.jobsStore.checkForJobApplication({ jobId });
          }
        },
        true
      );
      if (action.tree.routerStore.isCompanyRoute) {
        action.tree.jobsStore.getJobAccessPolicies(jobId);
      }
    },
    abort: ({ action }: HandlerArgs) => {
      action.tree.jobsStore.abort();
    }
  },
  [ROUTES.jobs.job]: {
    exact: true,
    call: ({ action, match }: HandlerArgs) => {
      const { jobId } = match.params;
      if (jobId === 'new') return;

      observe(
        action.tree.userStore,
        'isCandidate',
        change => {
          if (change.newValue) {
            action.tree.jobsStore.checkForJobApplication({ jobId });
          }
        },
        true
      );
      if (action.tree.routerStore.isCompanyRoute) {
        action.tree.jobsStore.getJobAccessPolicies(jobId);
      }
      action.tree.jobsStore.fetchAnonymousJob(jobId);
    },
    abort: ({ action }: HandlerArgs) => {
      action.tree.jobsStore.abort();
    }
  },
  [ROUTES.employer.jobs.new]: jobFlow,
  [ROUTES.employer.jobs.edit]: jobFlow,
  [ROUTES.employer.jobs.clone]: jobFlow,
  [ROUTES.employer.jobs.applicants.base]: {
    exact: false,
    call: ({ action, match }: HandlerArgs) => {
      const { jobId } = match.params;

      action.tree.jobsStore.fetchJob(jobId);
    },
    abort: ({ action }: HandlerArgs) => {
      action.tree.jobsStore.abort();
    }
  },
  [ROUTES.employer.jobs.application]: {
    call: ({ action, match }: HandlerArgs) => {
      const { jobId } = match.params;
      action.tree.jobsStore.fetchJob(jobId);
      action.tree.jobsStore.getJobApplicationSettings(match.params.jobId);
    },
    abort: ({ action }: HandlerArgs) => {
      action.tree.jobsStore.abort();
    }
  },
  [ROUTES.employer.jobs.suggestions]: {
    call: ({ action, match }: HandlerArgs) => {
      const { jobId } = match.params;
      action.tree.jobsStore.fetchJob(jobId);
      action.tree.resourcesStore.fetchAllThemes();
      action.tree.resourcesStore.getLanguages();
      action.tree.jobsStore.getJSS(match.params.jobId);
    },
    abort: ({ action }: HandlerArgs) => {
      action.tree.resourcesStore.abort();
      action.tree.jobsStore.abort();
    }
  },
  [ROUTES.employer.jobs.advertising]: {
    call: ({ action, match }: HandlerArgs) => {
      const { jobId } = match.params;
      action.tree.jobsStore.fetchJob(jobId);
      action.tree.resourcesStore.getBoards({ jobId: match.params.jobId });
      action.tree.jobsStore.getJobAdvertising({ jobId: match.params.jobId });
    },
    abort: ({ action }: HandlerArgs) => {
      action.tree.resourcesStore.abort();
      action.tree.jobsStore.abort();
    }
  },
  [ROUTES.employer.jobs.members]: {
    call: ({ action, match }: HandlerArgs) => {
      const { jobId } = match.params;
      action.tree.jobsStore.fetchJob(jobId);
      action.tree.jobsStore.getJobMembers({ jobId: match.params.jobId });
    },
    abort: ({ action }: HandlerArgs) => {
      action.tree.jobsStore.abort();
    }
  },
  [ROUTES.employer.jobs.feeds]: {
    call: ({ action, match }: HandlerArgs) => {
      const { jobId } = match.params;
      action.tree.jobsStore.fetchJob(jobId);
      action.tree.jobsStore.getJobFeeds({ jobId: match.params.jobId });
    },
    abort: ({ action }: HandlerArgs) => {
      action.tree.jobsStore.abort();
    }
  }
};

export default jobsFlows;
